// ═══════════════════════════════════════════════════════════════
// MainStreet — Contacts CRUD
// Table with org filter, modal for create/edit
// ═══════════════════════════════════════════════════════════════

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Badge, Button, TextInput, Select, Switch,
  Textarea, ActionIcon, Modal, Stack, SimpleGrid,
} from '@mantine/core';
import {
  IconSearch, IconPlus, IconPencil, IconTrash, IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useMainStreetStore } from '../../store/mainstreetStore';
import type { MSContact } from '../../store/mainstreetStore';
import { formatPhone } from '../../lib/phoneFormat';

const CONTACT_TYPES = [
  { value: 'primary', label: 'Primary' },
  { value: 'emergency', label: 'Emergency' },
  { value: 'technical', label: 'Technical' },
  { value: 'management', label: 'Management' },
];

const EMPTY: Partial<MSContact> = {
  orgId: '', name: '', title: '', dept: '', type: '',
  email: '', phone: '', altPhone: '', canAuth: 'false', notes: '',
};

export default function Contacts() {
  const {
    organizations, contacts, loadingContacts,
    loadOrganizations, loadContacts,
    createContact, updateContact, deleteContact,
  } = useMainStreetStore();

  const [search, setSearch] = useState('');
  const [orgFilter, setOrgFilter] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };
  const sortIcon = (f: string) => sortField === f ? (sortDir === 'asc' ? ' ↑' : ' ↓') : '';

  const { orgLists, loadOrgLists } = useMainStreetStore();
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MSContact | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MSContact | null>(null);
  const [form, setForm] = useState<Partial<MSContact>>({ ...EMPTY });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadOrganizations();
    loadContacts();
    loadOrgLists();
  }, []);

  const orgSelect = useMemo(() =>
    organizations.map(o => ({ value: String(o.id), label: o.name || `Org #${o.id}` })),
  [organizations]);

  const orgFilterSelect = useMemo(() =>
    [{ value: 'all', label: 'All Organizations' }, ...orgSelect],
  [orgSelect]);

  const listOrgIds = useMemo(() => {
    if (listFilter === 'all') return null;
    const l = orgLists.find(x => String(x.id) === listFilter);
    return l ? new Set(l.orgIds.split(',').filter(Boolean)) : null;
  }, [listFilter, orgLists]);

  const listFilterSelect = useMemo(() => [
    { value: 'all', label: 'All Lists' },
    ...orgLists.map(l => ({ value: String(l.id), label: `${l.name} (${l.orgIds.split(',').filter(Boolean).length})` })),
  ], [orgLists]);

  const filtered = useMemo(() => {
    let list = contacts;
    if (listOrgIds) list = list.filter(c => listOrgIds.has(c.orgId));
    if (orgFilter && orgFilter !== 'all') list = list.filter(c => c.orgId === orgFilter);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.title || '').toLowerCase().includes(q) ||
        (c.dept || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').includes(q),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (!sortField) return a.name.localeCompare(b.name);
      const va = (a as any)[sortField] ?? '';
      const vb = (b as any)[sortField] ?? '';
      return String(va).localeCompare(String(vb)) * dir;
    });
  }, [contacts, orgFilter, search, listOrgIds, sortField, sortDir]);

  const orgName = (id: string) => organizations.find(o => String(o.id) === id)?.name ?? id;

  const setField = (key: keyof MSContact, value: string) =>
    setForm(prev => ({ ...prev, [key]: value }));

  const openCreate = () => {
    setEditTarget(null);
    setForm({ ...EMPTY });
    setModalOpen(true);
  };

  const openEdit = (contact: MSContact) => {
    setEditTarget(contact);
    setForm({ ...contact });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      notifications.show({ message: 'Contact name is required', color: 'yellow' });
      return;
    }
    setSaving(true);
    try {
      if (editTarget) {
        await updateContact(editTarget.id, form);
        notifications.show({ message: 'Contact updated', color: 'green' });
      } else {
        await createContact(form);
        notifications.show({ message: 'Contact created', color: 'green' });
      }
      setModalOpen(false);
      setEditTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Save failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      await deleteContact(deleteTarget.id);
      notifications.show({ message: `${deleteTarget.name} deleted`, color: 'gray' });
      setDeleteTarget(null);
    } catch (err: unknown) {
      notifications.show({ message: err instanceof Error ? err.message : 'Delete failed', color: 'red' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Contacts</Title>
        <Group gap="xs">
          <Button size="xs" variant="light" leftSection={<IconRefresh size={14} />}
            loading={loadingContacts} onClick={() => loadContacts()}>Refresh</Button>
          <Button size="xs" leftSection={<IconPlus size={14} />} onClick={openCreate}>Add Contact</Button>
        </Group>
      </Group>

      <Group mb="md" gap="sm">
        <TextInput placeholder="Search name, title, dept, email, phone..."
          leftSection={<IconSearch size={14} />} value={search}
          onChange={e => setSearch(e.currentTarget.value)} size="xs"
          style={{ flex: 1, minWidth: 200 }} />
        <Select data={orgFilterSelect} value={orgFilter ?? 'all'}
          onChange={v => setOrgFilter(v)} size="xs" w={220} />
        {orgLists.length > 0 && (
          <Select data={listFilterSelect} value={listFilter}
            onChange={v => setListFilter(v ?? 'all')} size="xs" w={200} />
        )}
      </Group>

      <Text size="xs" c="dimmed" mb="xs">{filtered.length} contact{filtered.length !== 1 ? 's' : ''}</Text>

      <Paper withBorder>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--mantine-color-gray-3)' }}>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('name')}>Name{sortIcon('name')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('orgId')}>Organization{sortIcon('orgId')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('title')}>Title{sortIcon('title')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('dept')}>Dept{sortIcon('dept')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left', cursor: 'pointer' }} onClick={() => toggleSort('contactType')}>Type{sortIcon('contactType')}</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Email</th>
                <th style={{ padding: '8px 12px', textAlign: 'left' }}>Phone</th>
                <th style={{ padding: '8px 12px', textAlign: 'center' }}>Auth</th>
                <th style={{ padding: '8px 12px', textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ padding: 24, textAlign: 'center', color: '#888' }}>
                    {loadingContacts ? 'Loading...' : 'No contacts found'}
                  </td>
                </tr>
              )}
              {filtered.map(contact => (
                <tr key={contact.id} style={{ borderBottom: '1px solid var(--mantine-color-gray-2)' }}>
                  <td style={{ padding: '8px 12px', fontWeight: 500 }}>{contact.name || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{orgName(contact.orgId)}</td>
                  <td style={{ padding: '8px 12px' }}>{contact.title || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{contact.dept || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>
                    {contact.type && <Badge size="xs" variant="light">{contact.type}</Badge>}
                  </td>
                  <td style={{ padding: '8px 12px' }}>{contact.email || '-'}</td>
                  <td style={{ padding: '8px 12px' }}>{contact.phone ? formatPhone(contact.phone) : '-'}</td>
                  <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                    <Badge size="xs" color={contact.canAuth === 'true' ? 'green' : 'gray'}>
                      {contact.canAuth === 'true' ? 'Yes' : '-'}
                    </Badge>
                  </td>
                  <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon size="sm" variant="subtle" onClick={() => openEdit(contact)}>
                        <IconPencil size={14} />
                      </ActionIcon>
                      <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setDeleteTarget(contact)}>
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Group>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Paper>

      {/* Create / Edit Modal */}
      <Modal opened={modalOpen} onClose={() => setModalOpen(false)}
        title={editTarget ? 'Edit Contact' : 'Add Contact'} centered size="lg">
        <Stack>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <Select label="Organization" data={orgSelect} value={form.orgId || null}
              onChange={v => setField('orgId', v ?? '')} searchable clearable />
            <TextInput label="Name" required value={form.name ?? ''}
              onChange={e => setField('name', e.currentTarget.value)} />
            <TextInput label="Title" value={form.title ?? ''}
              onChange={e => setField('title', e.currentTarget.value)} />
            <TextInput label="Department" value={form.dept ?? ''}
              onChange={e => setField('dept', e.currentTarget.value)} />
            <Select label="Type" data={CONTACT_TYPES} value={form.type || null}
              onChange={v => setField('type', v ?? '')} clearable />
            <TextInput label="Email" value={form.email ?? ''}
              onChange={e => setField('email', e.currentTarget.value)} />
            <TextInput label="Phone" value={form.phone ?? ''}
              onChange={e => setField('phone', e.currentTarget.value)} />
            <TextInput label="Alt Phone" value={form.altPhone ?? ''}
              onChange={e => setField('altPhone', e.currentTarget.value)} />
          </SimpleGrid>
          <Switch label="Can Authorize" checked={form.canAuth === 'true'}
            onChange={e => setField('canAuth', e.currentTarget.checked ? 'true' : 'false')} />
          <Textarea label="Notes" value={form.notes ?? ''}
            onChange={e => setField('notes', e.currentTarget.value)} autosize minRows={2} />

          <Group justify="flex-end">
            <Button variant="default" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button loading={saving} onClick={handleSave}>
              {editTarget ? 'Save Changes' : 'Create Contact'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirmation */}
      <Modal opened={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Contact" centered size="sm">
        <Stack>
          <Text size="sm">Are you sure you want to delete <strong>{deleteTarget?.name}</strong>?</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button color="red" size="xs" loading={saving} onClick={handleDelete}>Delete</Button>
          </Group>
        </Stack>
      </Modal>
    </div>
  );
}
